import request from '@/utils/request'
import { isWechatBrowser } from '@/utils/wechatSilentAuth'

const CACHE_KEY = 'lastKnownLocation'
const CACHE_META_KEY = 'lastKnownLocationMeta'
export const LOCATION_ENABLED_KEY = 'deepark_location_enabled'
export const PRECISE_LOCATION_KEY = 'deepark_precise_location'
const CACHE_TTL_MS = 30 * 60 * 1000

export const DEFAULT_LOCATION = {
  latitude: 31.9522,
  longitude: 118.7789,
  address: '南京市江宁区潭园西路128号',
  isDefault: true
}

export function isLocationEnabled() {
  return localStorage.getItem(LOCATION_ENABLED_KEY) !== 'false'
}

export function setLocationEnabled(enabled) {
  localStorage.setItem(LOCATION_ENABLED_KEY, enabled ? 'true' : 'false')
}

export function isPreciseLocationEnabled() {
  return localStorage.getItem(PRECISE_LOCATION_KEY) !== 'false'
}

export function setPreciseLocationEnabled(enabled) {
  localStorage.setItem(PRECISE_LOCATION_KEY, enabled ? 'true' : 'false')
}

export function readCachedLocation(maxAgeMs = CACHE_TTL_MS) {
  try {
    const metaRaw = localStorage.getItem(CACHE_META_KEY)
    const locRaw = localStorage.getItem(CACHE_KEY)
    if (!metaRaw || !locRaw) return null

    const meta = JSON.parse(metaRaw)
    if (maxAgeMs >= 0 && Date.now() - meta.timestamp > maxAgeMs) {
      return null
    }

    const location = JSON.parse(locRaw)
    if (location?.latitude == null || location?.longitude == null) {
      return null
    }

    return {
      latitude: Number(location.latitude),
      longitude: Number(location.longitude),
      fromCache: true,
      stale: maxAgeMs < 0 || Date.now() - meta.timestamp > CACHE_TTL_MS
    }
  } catch (error) {
    console.warn('readCachedLocation failed:', error)
    return null
  }
}

export function saveCachedLocation(location, source = 'unknown') {
  if (location?.latitude == null || location?.longitude == null) return

  localStorage.setItem(CACHE_KEY, JSON.stringify({
    latitude: location.latitude,
    longitude: location.longitude
  }))
  localStorage.setItem(CACHE_META_KEY, JSON.stringify({
    timestamp: Date.now(),
    source
  }))
}

function getWx() {
  return typeof window !== 'undefined' ? window.wx : null
}

let wxConfigPromise = null

async function ensureWxConfig() {
  const wx = getWx()
  if (!wx) {
    throw new Error('WeChat JS-SDK unavailable')
  }

  if (wxConfigPromise) {
    return wxConfigPromise
  }

  wxConfigPromise = (async () => {
    const pageUrl = window.location.href.split('#')[0]
    const res = await request({
      url: '/auth/js-config',
      method: 'get',
      params: { url: pageUrl },
      headers: { isToken: false }
    })

    if (res.code !== 200 || !res.data) {
      throw new Error(res.msg || '获取微信 JS-SDK 配置失败')
    }

    const cfg = res.data
    await new Promise((resolve, reject) => {
      wx.config({
        debug: false,
        appId: cfg.appId,
        timestamp: cfg.timestamp,
        nonceStr: cfg.nonceStr,
        signature: cfg.signature,
        jsApiList: ['getLocation']
      })
      wx.ready(resolve)
      wx.error(reject)
    })
  })().catch((error) => {
    wxConfigPromise = null
    throw error
  })

  return wxConfigPromise
}

function getLocationViaWx() {
  return ensureWxConfig()
    .then(() => {
      const wx = getWx()
      return new Promise((resolve, reject) => {
        wx.getLocation({
          type: 'gcj02',
          success(res) {
            resolve({
              latitude: res.latitude,
              longitude: res.longitude,
              source: 'wechat'
            })
          },
          fail(err) {
            reject(err)
          }
        })
      })
    })
}

export function getCurrentPosition(options = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'))
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => resolve(position),
      (error) => reject(error),
      {
        enableHighAccuracy: options.enableHighAccuracy !== false,
        timeout: options.timeout ?? 15000,
        maximumAge: options.maximumAge ?? 5 * 60 * 1000,
        ...options
      }
    )
  })
}

function getLocationViaBrowser(options = {}) {
  const precise = isPreciseLocationEnabled()
  return getCurrentPosition({
    enableHighAccuracy: precise,
    timeout: 15000,
    maximumAge: options.maximumAge ?? 5 * 60 * 1000
  }).then((position) => ({
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    source: 'browser'
  }))
}

/**
 * Resolve user location with cache-first strategy.
 * @param {{ forceRefresh?: boolean, allowDefault?: boolean, silent?: boolean }} options
 */
export async function resolveUserLocation(options = {}) {
  const {
    forceRefresh = false,
    allowDefault = true,
    silent = false
  } = options

  if (!forceRefresh) {
    const cached = readCachedLocation()
    if (cached) {
      return cached
    }
  }

  if (!isLocationEnabled()) {
    if (allowDefault) {
      return { ...DEFAULT_LOCATION }
    }
    throw new Error('Location disabled by user')
  }

  let location = null
  let lastError = null

  if (isWechatBrowser()) {
    try {
      location = await getLocationViaWx()
    } catch (error) {
      console.warn('wx.getLocation failed, fallback to browser geolocation:', error)
      lastError = error
    }
  }

  if (!location) {
    try {
      location = await getLocationViaBrowser({
        maximumAge: forceRefresh ? 0 : 5 * 60 * 1000
      })
    } catch (error) {
      lastError = error
    }
  }

  if (location) {
    saveCachedLocation(location, location.source)
    return location
  }

  const staleCache = readCachedLocation(-1)
  if (staleCache) {
    return staleCache
  }

  if (allowDefault) {
    return { ...DEFAULT_LOCATION }
  }

  if (!silent) {
    throw lastError || new Error('Failed to get location')
  }

  return null
}
